/**
 * Generic skeleton block + an `ArticleCardSkeleton` preset shaped to match
 * `<ArticleCard>`. Uses the `.skeleton-shimmer` utility (defined in
 * src/index.css) — a 1.5s linear gradient sweep that respects
 * `prefers-reduced-motion` via the global CSS rule.
 *
 * @param {object} props
 * @param {string} [props.className]
 * @param {string} [props.shape='rect']  'rect' | 'circle'
 * @param {string|number} [props.width]
 * @param {string|number} [props.height]
 *
 * @example
 *   <Skeleton width="100%" height={24} />
 *   <ArticleCardSkeleton />
 */
export default function Skeleton({
  className = '',
  shape = 'rect',
  width,
  height,
  style,
  ...rest
}) {
  return (
    <span
      role="status"
      aria-hidden="true"
      className={`skeleton-shimmer block ${shape === 'circle' ? 'rounded-full' : 'rounded-md'} ${className}`}
      style={{ width, height, ...style }}
      {...rest}
    />
  );
}

/**
 * ArticleCard-shaped skeleton — drop-in placeholder while a card list loads.
 *
 * @example
 *   <div className="grid gap-6 md:grid-cols-3">
 *     {Array.from({ length: 3 }).map((_, i) => <ArticleCardSkeleton key={i} />)}
 *   </div>
 */
export function ArticleCardSkeleton() {
  return (
    <div className="card overflow-hidden" aria-hidden="true">
      <Skeleton className="aspect-video w-full !rounded-none" />
      <div className="p-5 space-y-3">
        <Skeleton width="35%" height={12} />
        <Skeleton width="90%" height={22} />
        <Skeleton width="75%" height={22} />
        <div className="flex items-center gap-2 pt-3">
          <Skeleton shape="circle" width={24} height={24} />
          <Skeleton width="40%" height={12} />
        </div>
      </div>
    </div>
  );
}
